/**
 * Meta Pixel event tracking utility
 * Fires standard and custom Facebook/Meta pixel events
 */

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}

function isFbqReady(): boolean {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
}

/** Fire a standard Meta Pixel event */
export function trackMetaEvent(eventName: string, params?: Record<string, any>) {
  if (!isFbqReady()) return;
  if (params) {
    window.fbq('track', eventName, params);
  } else {
    window.fbq('track', eventName);
  }
}

/** Fire a custom Meta Pixel event */
export function trackMetaCustomEvent(eventName: string, params?: Record<string, any>) {
  if (!isFbqReady()) return;
  if (params) {
    window.fbq('trackCustom', eventName, params);
  } else {
    window.fbq('trackCustom', eventName);
  }
}

// ── Standard event helpers ──

export const metaPixel = {
  /** User completes registration / sign-up */
  completeRegistration: (params?: { content_name?: string; status?: string }) =>
    trackMetaEvent('CompleteRegistration', params),

  /** User signs up for a trial or free tier */
  lead: (params?: { content_name?: string; content_category?: string }) =>
    trackMetaEvent('Lead', params),

  /** User starts checkout / upgrade flow */
  initiateCheckout: (params?: { value?: number; currency?: string; content_ids?: string[] }) =>
    trackMetaEvent('InitiateCheckout', { currency: 'INR', ...params }),

  /** User completes purchase / subscription */
  purchase: (params: { value: number; currency?: string; content_name?: string }) =>
    trackMetaEvent('Purchase', { currency: 'INR', ...params }),

  /** User starts a trial */
  startTrial: (params?: { value?: number; currency?: string; predicted_ltv?: number }) =>
    trackMetaEvent('StartTrial', { currency: 'INR', ...params }),

  /** User views important content (e.g. pricing page) */
  viewContent: (params?: { content_name?: string; content_category?: string }) =>
    trackMetaEvent('ViewContent', params),

  /** User searches */
  search: (params?: { search_string?: string }) =>
    trackMetaEvent('Search', params),

  // ── Custom events for Careerfy ──

  /** User uploads resume */
  resumeUploaded: () =>
    trackMetaCustomEvent('ResumeUploaded'),

  /** User starts an interview session */
  interviewStarted: (params?: { job_role?: string }) =>
    trackMetaCustomEvent('InterviewStarted', params),

  /** User completes an interview session */
  interviewCompleted: (params?: { job_role?: string; score?: number }) =>
    trackMetaCustomEvent('InterviewCompleted', params),

  /** User starts career DNA assessment */
  careerAssessmentStarted: () =>
    trackMetaCustomEvent('CareerAssessmentStarted'),

  /** User completes career DNA assessment */
  careerAssessmentCompleted: () =>
    trackMetaCustomEvent('CareerAssessmentCompleted'),

  /** User starts resume builder */
  resumeBuilderStarted: () =>
    trackMetaCustomEvent('ResumeBuilderStarted'),

  /** User downloads resume */
  resumeDownloaded: () =>
    trackMetaCustomEvent('ResumeDownloaded'),

  // ── New standard events ──

  /** User enters payment details (Razorpay checkout) */
  addPaymentInfo: (params?: { content_category?: string; content_ids?: string[]; value?: number }) =>
    trackMetaEvent('AddPaymentInfo', { currency: 'INR', ...params }),

  /** User completes a paid subscription */
  subscribe: (params?: { value?: number; currency?: string; predicted_ltv?: number }) =>
    trackMetaEvent('Subscribe', { currency: 'INR', ...params }),

  /** User submits resume for analysis or starts interview */
  submitApplication: (params?: { content_name?: string; content_category?: string }) =>
    trackMetaEvent('SubmitApplication', params),

  /** User contacts support / submits feedback */
  contact: (params?: { content_name?: string }) =>
    trackMetaEvent('Contact', params),

  /** User books a consultation / mentorship call */
  schedule: (params?: { content_name?: string }) =>
    trackMetaEvent('Schedule', params),

  /** User customizes resume in builder */
  customizeProduct: (params?: { content_name?: string; content_category?: string }) =>
    trackMetaEvent('CustomizeProduct', params),
};
